import { Box } from "@material-ui/core"
import {
  DEFAULT_LOOP_SECTIONS,
  ClientDetailPage,
  LoopShell,
  PreparePanel,
  ViraThemeProvider,
  ViraText,
  useViraColorMode,
  type ClientChip,
  type LoopNavSection,
} from "@ksana/vira-ui"
import { ReactNode, useMemo, type MutableRefObject } from "react"
import type { MouseEvent as ReactMouseEvent } from "react"

export type LoopNavId =
  | "prepare"
  | "new-session"
  | "summary"
  | "calendar"
  | "sleep"
  | "activity"
  | "language"
  | "assessments"
  | "dbt-diary"
  | "safety-plan"
  | "relapse"
  | "past-sessions"
  | "plan-and-note"
  | "account-settings"

/** Default Loop pane when opening `/client-detail/:id` with Loop enabled. */
export const DEFAULT_LOOP_NAV: LoopNavId = "prepare"

/** Leaf ids from `DEFAULT_LOOP_SECTIONS` (valid URL path segments). */
export const LOOP_NAV_IDS: ReadonlySet<LoopNavId> = new Set([
  "prepare",
  "new-session", // legacy — redirected to prepare
  "summary",
  "calendar",
  "sleep",
  "activity",
  "language",
  "assessments",
  "dbt-diary",
  "safety-plan",
  "relapse",
  "past-sessions",
  "plan-and-note", // legacy — redirected to past-sessions Progress Note
  "account-settings",
])

export function isLoopNavId(
  value: string | undefined | null
): value is LoopNavId {
  return Boolean(value && LOOP_NAV_IDS.has(value as LoopNavId))
}

export function parseLoopNavId(
  value: string | undefined | null
): LoopNavId | null {
  return isLoopNavId(value) ? value : null
}

export type LoopPaneVisibility = {
  showSafetyPlan: boolean
  showRelapse: boolean
  showPastSessions: boolean
  showPlanAndNote: boolean
  showDbtDiary: boolean
}

/** Figma Loop page chrome: eyebrow + title for Treat / Wrap-up leaves. */
export type ClientDetailPaneChrome = {
  eyebrow: string
  title: string
}

/**
 * Page header for Treat / Wrap-up panes via shared `ClientDetailPage` chrome.
 * Prepare still owns its header inside PreparePanel — return null there.
 */
export function getClientDetailPaneChrome(
  nav: LoopNavId,
  clientName: string
): ClientDetailPaneChrome | null {
  const treat = `${clientName} · Treat`

  switch (nav) {
    case "prepare":
    case "new-session":
      return null
    case "summary":
      return { eyebrow: treat, title: "Summary" }
    case "calendar":
      return { eyebrow: treat, title: "Calendar" }
    case "sleep":
      return { eyebrow: treat, title: "Sleep" }
    case "activity":
      return { eyebrow: treat, title: "Activity" }
    case "language":
      return { eyebrow: treat, title: "Language" }
    case "assessments":
      return { eyebrow: treat, title: "Assessments" }
    case "dbt-diary":
      return { eyebrow: treat, title: "DBT diary cards" }
    case "safety-plan":
      return { eyebrow: treat, title: "Safety plan" }
    case "relapse":
      return { eyebrow: treat, title: "Relapse prevention" }
    case "past-sessions":
      return { eyebrow: treat, title: "Sessions" }
    case "account-settings":
      return { eyebrow: "Account", title: "Account settings" }
    case "plan-and-note":
      // Legacy URL — Client Detail redirects to Sessions → Progress Note.
      return { eyebrow: treat, title: "Sessions" }
    default:
      return null
  }
}

type ClientDetailChromeProps = {
  clientName: string
  /** @deprecated Unused — account lives in the sidebar. */
  practitionerName?: string
  chips?: ClientChip[]
  activeItemId: LoopNavId
  visibility: LoopPaneVisibility
  onNavigate: (itemId: LoopNavId) => void
  onBackToClients: () => void
  onStartSession?: (event: ReactMouseEvent<HTMLElement>) => void
  onPauseSession?: () => void
  onResumeSession?: () => void
  onStopSession?: () => void
  sessionStatus?: "idle" | "recording" | "paused" | "uploading" | "processing"
  registerElapsedTimeElement?: (element: HTMLElement) => () => void
  audioVisualizerCanvasRef?: MutableRefObject<HTMLCanvasElement | null>
  onHeaderNav?: (id: string) => void
  onAccountSettings?: () => void
  children: ReactNode
}

export function buildLoopSections(
  visibility: LoopPaneVisibility
): LoopNavSection[] {
  const isVisible = (id: string): boolean => {
    switch (id) {
      case "safety-plan":
        return visibility.showSafetyPlan
      case "relapse":
        return visibility.showRelapse
      case "past-sessions":
        return visibility.showPastSessions
      case "plan-and-note":
        return false
      case "dbt-diary":
        return visibility.showDbtDiary
      default:
        return true
    }
  }

  return DEFAULT_LOOP_SECTIONS.map((section) => {
    if (!section.items) {
      return section
    }
    return {
      ...section,
      items: section.items.filter((item) => isVisible(item.id)),
    }
  }).filter((section) => {
    if (section.items) {
      return section.items.length > 0
    }
    return isVisible(section.id)
  })
}

export function ClientDetailComingSoon({
  title,
  eyebrow,
}: {
  title: string
  eyebrow?: string
}) {
  return (
    <ClientDetailPage eyebrow={eyebrow} title={title} maxWidth={640}>
      <Box style={{ padding: "0 0 8px" }}>
        <ViraText tone="muted" sx={{ fontSize: 14, lineHeight: 1.6 }}>
          This pane is coming soon. Use the sidebar to open Prepare, Summary,
          Assessments, or Sessions.
        </ViraText>
      </Box>
    </ClientDetailPage>
  )
}

export function ClientDetailPrepareFallback({
  clientName,
  onBriefSummary,
}: {
  clientName: string
  onBriefSummary?: () => void
}) {
  return (
    <PreparePanel
      state="initial"
      clientName={clientName}
      sources={[]}
      sections={[]}
      cardTitle="Sign in to load client context for session prep"
      onGenerate={onBriefSummary}
      onBriefSummary={onBriefSummary}
    />
  )
}

export default function ClientDetailChrome({
  clientName,
  chips = [],
  activeItemId,
  visibility,
  onNavigate,
  onBackToClients,
  onStartSession,
  onPauseSession,
  onResumeSession,
  onStopSession,
  sessionStatus,
  registerElapsedTimeElement,
  audioVisualizerCanvasRef,
  onHeaderNav,
  onAccountSettings,
  children,
}: ClientDetailChromeProps) {
  const { mode, setMode } = useViraColorMode()
  const sections = useMemo(() => buildLoopSections(visibility), [visibility])

  return (
    <ViraThemeProvider mode={mode}>
      <LoopShell
        header={{
          navItems: [
            {
              id: "techniques",
              label: "My Techniques and Interventions",
            },
            { id: "org", label: "My Organization" },
            { id: "clients", label: "My Clients", active: true },
          ].map((item) => ({
            ...item,
            onClick: () => onHeaderNav?.(item.id),
          })),
        }}
        sidebar={{
          clientName,
          chips,
          activeItemId,
          sections,
          onBackToClients,
          onStartSession,
          onPauseSession,
          onResumeSession,
          onStopSession,
          sessionStatus,
          registerElapsedTimeElement,
          audioVisualizerCanvasRef,
          onNavigate: (itemId) => onNavigate(itemId as LoopNavId),
          onAccountSettings,
          accountSettingsActive: activeItemId === "account-settings",
          themeMode: mode,
          onThemeModeChange: setMode,
        }}
      >
        {children}
      </LoopShell>
    </ViraThemeProvider>
  )
}
